import React, { useCallback, useEffect, useState } from "react";
import { api, fmt2 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { DollarSign, Clock, CheckCircle, ArrowUpCircle } from "lucide-react";

const PAYOUT_STATUS_COLORS = {
  pending: "bg-amber-50 text-amber-800",
  requested: "bg-sky-50 text-sky-800",
  approved: "bg-emerald-50 text-emerald-800",
  rejected: "bg-rose-50 text-rose-800",
  paid: "bg-purple-50 text-purple-800",
};

const BOOKING_STATUS_COLORS = {
  confirmed: "bg-sky-50 text-sky-800",
  paid: "bg-emerald-50 text-emerald-800",
  cancelled: "bg-rose-50 text-rose-800",
  refunded: "bg-amber-50 text-amber-800",
};

function SummaryCard({ label, value, Icon, color }) {
  return (
    <div className="rounded-2xl bg-card border border-border p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className={`h-9 w-9 rounded-xl flex items-center justify-center ${color}`}><Icon className="h-4 w-4" /></span>
      </div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
    </div>
  );
}

export default function VendorReconciliation() {
  const [data, setData] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [requesting, setRequesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, page_size: 20 });
      if (statusFilter !== "all") params.set("status", statusFilter);
      const [recon, pr] = await Promise.all([
        api.get(`/vendor/reconciliation?${params}`),
        api.get("/vendor/payout-requests"),
      ]);
      setData(recon.data);
      setPayouts(pr.data || []);
    } catch (e) { toast.error("Failed to load reconciliation data"); }
  }, [statusFilter, page]);

  useEffect(() => { load(); }, [load]);

  const requestPayout = async () => {
    setRequesting(true);
    try {
      await api.post("/vendor/payout-requests", { notes: "" });
      toast.success("Payout request submitted! Admin will review shortly.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit payout request"); }
    setRequesting(false);
  };

  const summary = data?.summary || { pending_net: 0, paid_out_net: 0, total_net: 0 };
  const hasPendingRequest = payouts.some(p => p.status === "pending" || p.status === "requested");

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8" data-testid="vendor-reconciliation-page">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Earnings & Payouts</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Your net earnings after IndoVoyage's 10% marketplace fee.</p>
        </div>
        <Button
          onClick={requestPayout}
          disabled={requesting || hasPendingRequest || summary.pending_net <= 0}
          className="rounded-xl bg-gradient-to-r from-[hsl(var(--vermilion))] to-[hsl(var(--saffron))] text-white hover:brightness-110"
          data-testid="request-payout-btn"
        >
          <ArrowUpCircle className="h-4 w-4 mr-2" />
          {hasPendingRequest ? "Request pending…" : requesting ? "Submitting…" : "Request payout"}
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <SummaryCard label="Pending payout" value={fmt2(summary.pending_net)} Icon={Clock} color="bg-amber-100 text-amber-700" />
        <SummaryCard label="Total paid out" value={fmt2(summary.paid_out_net)} Icon={CheckCircle} color="bg-emerald-100 text-emerald-700" />
        <SummaryCard label="All-time net earnings" value={fmt2(summary.total_net)} Icon={DollarSign} color="bg-sky-100 text-sky-700" />
      </div>

      {/* Payout request history */}
      {payouts.length > 0 && (
        <div className="rounded-2xl bg-card border border-border p-5 mb-6" data-testid="payout-requests-section">
          <h3 className="font-semibold mb-3">Payout Requests</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-left">
                <tr className="border-b border-border"><th className="pb-2">ID</th><th>Bookings</th><th>Net amount</th><th>Submitted</th><th>Status</th><th>Admin notes</th></tr>
              </thead>
              <tbody>
                {payouts.map(p => (
                  <tr key={p.payout_id} className="border-t border-border" data-testid="payout-request-row">
                    <td className="py-2 font-mono text-xs">{p.payout_id}</td>
                    <td>{p.booking_count}</td>
                    <td className="font-semibold">{fmt2(p.amount_net)}</td>
                    <td className="text-muted-foreground text-xs">{p.created_at?.slice(0, 10)}</td>
                    <td><span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${PAYOUT_STATUS_COLORS[p.status] || "bg-muted"}`}>{p.status}</span></td>
                    <td className="text-xs text-muted-foreground">{p.admin_notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Booking breakdown */}
      <div className="rounded-2xl bg-card border border-border overflow-x-auto" data-testid="vendor-booking-breakdown">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-semibold">Booking Breakdown</h3>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[150px]"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <table className="w-full text-sm">
          <thead className="text-muted-foreground text-left">
            <tr className="border-b border-border">
              <th className="p-3">Trip</th><th>Guest</th><th>Date</th><th>Status</th>
              <th className="text-right">Gross</th><th className="text-right">Fee (10%)</th>
              <th className="text-right">Your net</th><th className="text-right pr-3">Payout</th>
            </tr>
          </thead>
          <tbody>
            {!data ? (
              <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Loading…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">No bookings found.</td></tr>
            ) : data.items.map(b => (
              <tr key={b.booking_id} className="border-t border-border" data-testid="reconciliation-booking-row">
                <td className="p-3">
                  <div className="font-medium line-clamp-1">{b.title}</div>
                  <div className="text-xs font-mono text-muted-foreground">{b.booking_id}</div>
                </td>
                <td><div>{b.contact_name}</div></td>
                <td className="text-muted-foreground">{b.date}</td>
                <td><span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${BOOKING_STATUS_COLORS[b.status] || "bg-muted"}`}>{b.status}</span></td>
                <td className="text-right">{fmt2(b.amount_gross)}</td>
                <td className="text-right text-rose-600">-{fmt2(b.platform_fee)}</td>
                <td className="text-right font-semibold text-emerald-700">{fmt2(b.vendor_net)}</td>
                <td className="text-right pr-3"><span className={`text-xs px-2 py-0.5 rounded-full ${PAYOUT_STATUS_COLORS[b.payout_status] || "bg-muted text-foreground"}`}>{b.payout_status || "pending"}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && data.pages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-border text-sm">
            <span className="text-muted-foreground">{data.total} bookings</span>
            <div className="flex gap-2">
              <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-8 rounded-xl">Previous</Button>
              <Button variant="ghost" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} className="h-8 rounded-xl">Next</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
